import { IChatConversation, IChatSession } from '../@types/chat';
import { IGatheringChatItem } from '../features/ActionsBar/Gathering/GatheringChat/types';
import { RTCConnection } from '../provider/VoiceChannel/livekit/types';
import { axios } from '../utils/axios';

export type GuestChannelInfo = Pick<
  IChatConversation,
  'name' | 'participants' | 'workspace' | '_id'
> &
  RTCConnection & {
    externalLink: string;
    deviceId?: string;
  };

export async function generateExternalLink(
  channelId: string,
): Promise<{ externalLink: string }> {
  const { data } = await axios.post<{ externalLink: string }>(
    `chat/channel/create-external-link`,
    { channelId },
  );
  return data;
}

export async function refreshExternalLink(
  channelId: string,
): Promise<{ externalLink: string }> {
  const { data } = await axios.post<{ externalLink: string }>(
    `chat/channel/refresh-external-link`,
    { channelId },
  );
  return data;
}

export async function removeExternalLink(channelId: string): Promise<any> {
  const { data } = await axios.delete<any>(
    `chat/channel/delete-external-link/${channelId}`,
  );
  return data;
}

export async function getChannelInfoByExternalLink(
  externalLink: string,
): Promise<GuestChannelInfo> {
  const { data } = await axios.get<GuestChannelInfo>(
    `chat/channel/voice/external-link/info?externalLink=${externalLink}`,
  );
  return data;
}

export async function allowGuestUser(deviceId: string): Promise<any> {
  const { data } = await axios.post<{ deviceId: string }>(
    `/chat/channel/voice/admit-guest`,
    { deviceId },
  );
  return data;
}

export async function denyGuestUser(deviceId: string): Promise<any> {
  const { data } = await axios.post<{ deviceId: string }>(
    `/chat/channel/voice/deny-guest`,
    { deviceId },
  );
  return data;
}

export async function lockChannel(channelId: string): Promise<any> {
  const { data } = await axios.post<{ channelId: string }>(
    `/chat/channel/voice/lock`,
    { channelId },
  );
  return data;
}

export async function unlockChannel(channelId: string): Promise<any> {
  const { data } = await axios.post<{ channelId: string }>(
    `/chat/channel/voice/unlock`,
    { channelId },
  );
  return data;
}

export async function sendEmailInvitation(
  channelId: string,
  emails: string[],
): Promise<any> {
  const { data } = await axios.post<any>(
    `/chat/channel/voice/email-invitation`,
    { channelId, emails },
  );
  return data;
}

// GATHERING SESSION AND CHAT

export async function getGatheringSessionById(sessionId?: string) {
  const { data } = await axios.get<IChatSession>(
    `/chat/channel/voice/session/${sessionId}`,
  );
  return data;
}

export async function getGatheringChat(sessionRef: string) {
  const { data } = await axios.get<IChatConversation>(
    `/chat/session/comment/room/${sessionRef}`,
  );
  return data;
}

export async function fetchGatheringChatMessages(
  sessionRef: string,
  params: { before?: string },
) {
  const { data } = await axios.get<{ items: IGatheringChatItem[] }>(
    `/chat/session/comment/list/${sessionRef}`,
    { params },
  );
  return data.items;
}

export type AddGatheringCHatMessagetPayload = Pick<
  IGatheringChatItem,
  | 'content'
  | 'sessionRef'
  | 'contentType'
  | 'mimeType'
  | 'fileName'
  | 'fileSize'
>;

export async function sendGatheringChatMessage(
  payload: AddGatheringCHatMessagetPayload,
) {
  const { data } = await axios.post<IGatheringChatItem>(
    `/chat/session/comment/send`,
    payload,
  );
  return data;
}

export type RemoveGatheringChatMessagetPayload = Pick<
  IGatheringChatItem,
  '_id' | 'sessionRef'
>;

// TODO - VERIFICAR ROTA
export async function deleteGatheringChatMessage({
  _id,
  sessionRef,
}: RemoveGatheringChatMessagetPayload) {
  const { data } = await axios.delete<IGatheringChatItem>(
    `/chat/session/comment/delete?sessionRef=${sessionRef}&messageId=${_id}`,
  );
  return data;
}

export type EditGatheringChatMessagePayload = {
  content: string;
  messageId: string;
  sessionRef: string;
};

export async function editGatheringChatMessage(
  payload: EditGatheringChatMessagePayload,
) {
  const { data } = await axios.put<IGatheringChatItem>(
    `/chat/session/comment/edit`,
    payload,
  );
  return data;
}

export type ReactGatheringChatMessagePayload = {
  reaction: string;
} & Pick<EditGatheringChatMessagePayload, 'sessionRef' | 'messageId'>;

export async function reactGatheringChatMessage(
  payload: ReactGatheringChatMessagePayload,
) {
  const { data } = await axios.patch<IGatheringChatItem>(
    `/chat/session/comment/reaction`,
    payload,
  );
  return data;
}

export type ReadGatheringChatMessagePayload = Pick<
  EditGatheringChatMessagePayload,
  'sessionRef' | 'messageId'
>;

export async function readGatheringChatMessage(
  payload: ReadGatheringChatMessagePayload,
) {
  const { data } = await axios.put<IGatheringChatItem>(
    `/chat/session/comment/last-read`,
    payload,
  );
  return data;
}

export * as GatheringRequests from './gathering';
